import { plainToInstance } from "class-transformer";
import axios, { AxiosRequestConfig, AxiosResponse, RawAxiosRequestHeaders } from "axios";
import Portfolio from "./models/portfolio";
import User from "./models/user";
import Position from "./models/portfolioPosition";
import PortfolioComposite from "./models/portfolioComposite";
import AssetType from "./models/fixProtocol/instrument/assetType";
import Security from "./models/fixProtocol/instrument/security";
import LastPrice from "./models/lastPrice";
import { ValuationMultiple } from "./models/valuation/valuationMultiple";

const client = axios.create({
  baseURL: process.env.REACT_APP_API_SERVER_URL
});

const authHeaders = (accessToken: string): AxiosRequestConfig => {
  return {
    headers: {
      Authorization: `Bearer ${accessToken}`
    } as RawAxiosRequestHeaders
  };
}

const getUser = async (accessToken: string) => {
  try {
    const response: AxiosResponse = await client.get<User | undefined>(`/api/v0/users`, authHeaders(accessToken));
    const status = response.status;
    if (status < 300) {
      const data = response.data;
      const user = plainToInstance(User, data as User, { exposeUnsetFields: true });
      if (user instanceof User) {
        return user;
      } else {
        return undefined;
      }
    }
    throw new Error(`Some problem occurred on getUser(). response: ${JSON.stringify(response)}`);
  } catch (e) {
    console.log(e);
    throw e;
  }
}

/**
 * Creates a user.
 * @param accessToken The access token provided by the authorization server.
 * @param authServerUserId The id of the user in the authentication server.
 * @param name 
 * @param email 
 * @returns If successful return the newly created user id, otherwise return undefined.
 */
const createUser = async (accessToken: string, authServerUserId: string, name: string, email: string) => {
  try {
    const body = new User('', authServerUserId, name, '', email, '');
    const response: AxiosResponse = await client.post<string>(`/api/v0/users`, body, authHeaders(accessToken));
    const status = response.status;
    if (status < 300) {
      return response.data;
    }
    else {
      return undefined;
    }
  } catch (e) {
    console.log(e);
    throw e;
  }
}

const getPortfoliosByUser = async (accessToken: string, userId: string) => {
  try {
    const response: AxiosResponse = await client.get<Portfolio>(`/api/v0/users/${userId}/portfolios`, authHeaders(accessToken));
    const status = response.status;
    if (status < 300) {
      const data = response.data;
      const portfolios = plainToInstance(Portfolio, data as [], { exposeUnsetFields: true, });
      portfolios.forEach(p => p.recalculateValues());
      portfolios.forEach(p => p.portfolioComposite?.sort());
      if (portfolios[0] && portfolios[0] instanceof Portfolio) {
        return portfolios;
      } else {
        return [];
      }
    }
    throw new Error(`Some problem occurred on getPortfoliosByUser(). response: ${JSON.stringify(response)}`);
  } catch (e) {
    console.log(e);
    return [];
  }
};

const createPortfolio = async (accessToken: string, userId: string, portfolio: Portfolio) => {
  try {
    const body = portfolio;
    const response: AxiosResponse = await client.post<string>(`/api/v0/users/${userId}/portfolios`, body, authHeaders(accessToken));
    const status = response.status;
    if (status < 300) {
      return response.data;
    }
    throw new Error(`Some problem occurred on createPortfolio(). response: ${JSON.stringify(response)}`);
  } catch (e) {
    console.log(e);
    return [];
  }
};

const getAssetTypes = async (accessToken: string) => {
  try {
    const response = await client.get<AssetType[]>(`/api/v0/asset-types`, authHeaders(accessToken));
    const status = response.status;
    if (status < 300) {
      const data = response.data;
      const assetTypes = plainToInstance(AssetType, data as [], { exposeUnsetFields: true, });
      return assetTypes;
    }
    throw new Error(`Some problem occurred on getAssetTypes(). response: ${JSON.stringify(response)}`);
  } catch (e) {
    console.log(e);
    return [];
  }
};

const getSecurities = async (accessToken: string, assetTypeId: string, symbolFilter: string) => {
  try {
    const response = await client.get<Security[]>(`/api/v0/asset-types/${assetTypeId}/securities?symbol=${symbolFilter}`, authHeaders(accessToken));
    const status = response.status;
    if (status < 300) {
      const data = response.data;
      const securities = plainToInstance(Security, data as [], { exposeUnsetFields: true, });
      return securities;
    }
    throw new Error(`Some problem occurred on getSecurities(). response: ${JSON.stringify(response)}`);
  } catch (e) {
    console.log(e);
    return [];
  }
};

const getSecuritiesByCnpj = async (accessToken: string, assetTypeId: string, cnpjFilter: string) => {
  try {
    const response = await client.get<Security[]>(`/api/v0/asset-types/${assetTypeId}/securities?cnpj=${cnpjFilter}`, authHeaders(accessToken));
    const status = response.status;
    if (status < 300) {
      const data = response.data;
      const securities = plainToInstance(Security, data as [], { exposeUnsetFields: true, });
      return securities;
    }
    throw new Error(`Some problem occurred on getSecuritiesByCnpj(). response: ${JSON.stringify(response)}`);
  } catch (e) {
    console.log(e);
    return [];
  }
};

const getLastPrice = async (accessToken: string, securityId: string) => {
  try {
    const response = await client.get<LastPrice>(`/api/v0/last-prices/${securityId}`, authHeaders(accessToken));
    const status = response.status;
    if (status < 300) {
      const data = response.data;
      const securities = plainToInstance(LastPrice, data, { exposeUnsetFields: true, });
      return securities;
    }
    throw new Error(`Some problem occurred on getLastPrice(). response: ${JSON.stringify(response)}`);
  } catch (e) {
    console.log(e);
    return undefined;
  }
};

const getPriceToEarnings = async (accessToken: string, symbol: string, dateStart = new Date(Date.UTC(2010, 0, 1)), dateEnd = new Date()) => {
  try {
    const response = await client.get<ValuationMultiple>(`/api/v0/stock-data/valuation/price-to-earnings?symbol=${symbol}&dateStart=${dateStart.toISOString()}&dateEnd=${dateEnd.toISOString()}`, authHeaders(accessToken));
    const status = response.status;
    if (status < 300) {
      const data = response.data;
      const priceToEarnings = plainToInstance(ValuationMultiple, data, { exposeUnsetFields: true, });
      return priceToEarnings;
    }
    throw new Error(`Some problem occurred on getPriceToEarnings(). response: ${JSON.stringify(response)}`);
  } catch (e) {
    console.log(e);
    return undefined;
  }
};

const getEnterpriseValueToEbitda = async (accessToken: string, symbol: string, dateStart = new Date(Date.UTC(2010, 0, 1)), dateEnd = new Date()) => {
  try {
    const response = await client.get<ValuationMultiple>(`/api/v0/stock-data/valuation/enterprise-value-to-ebitda?symbol=${symbol}&dateStart=${dateStart.toISOString()}&dateEnd=${dateEnd.toISOString()}`, authHeaders(accessToken));
    const status = response.status;
    if (status < 300) {
      const data = response.data;
      const priceToEarnings = plainToInstance(ValuationMultiple, data, { exposeUnsetFields: true, });
      return priceToEarnings;
    }
    throw new Error(`Some problem occurred on getEnterpriseValueToEbitda(). response: ${JSON.stringify(response)}`);
  } catch (e) {
    console.log(e);
    return undefined;
  }
};

/**
 * Updates or inserts a composite.
 * @param accessToken The access token to be used in the upsert api call.
 * @param userId The id of the user to which the composite belongs to.
 * @param composite The composite to be userted.
 * @returns The id of the upserted composite.
 */
const upsertComposite = async (accessToken: string, userId: string, composite: PortfolioComposite) => {
  try {
    const body = composite;
    const response = await client.post<string>(`/api/v0/users/${userId}/portfolios/${composite.portfolioId}/composites`, body, authHeaders(accessToken));
    const status = response.status;
    if (status < 300) {
      if (composite.id) {
        return composite.id;
      } else {
        return response.data;
      }
    }
    throw new Error(`Some problem occurred on upsertComposite(). response: ${JSON.stringify(response)}`);
  } catch (e) {
    console.log(e);
    throw e;
  }
}

/**
 * Deletes a composite.
 * @param accessToken The access token to be used in the delete api call.
 * @param userId The id of the user to which the composite belongs to.
 * @param compositeId The id of the composite to be deleted.
 * @returns True if successful, false otherwise.
 */
const deleteComposite = async (accessToken: string, userId: string, composite: PortfolioComposite) => {
  try {
    const response = await client.delete<boolean>(`/api/v0/users/${userId}/portfolios/${composite.portfolioId}/composites/${composite.id}`, authHeaders(accessToken));
    const status = response.status;
    if (status < 300) {
      return response.data;
    }
    throw new Error(`Some problem occurred on deleteComposite(). response: ${JSON.stringify(response)}`);
  } catch (e) {
    console.log(e);
    throw e;
  }
}

/**
 * Updates or inserts a position.
 * @param accessToken The access token to be used in the upsert api call.
 * @param userId The id of the user to which the position belongs to.
 * @param position The position to be userted.
 * @returns The id of the upserted position.
 */
const upsertPosition = async (accessToken: string, userId: string, position: Position) => {
  try {
    const body = position;
    let response = undefined;
    let status = undefined;
    if (!position.id) {
      response = await client.post<string>(`/api/v0/users/${userId}/portfolios/${position.portfolioId}/positions`, body, authHeaders(accessToken));
      status = response.status;
      if (status < 300) {
        return response.data;
      }
    } else {
      response = await client.put<boolean>(`/api/v0/users/${userId}/portfolios/${position.portfolioId}/positions/${position.id}`, body, authHeaders(accessToken));
      status = response.status;
      if (status < 300) {
        return position.id
      }
    }
    throw new Error(`Some problem occurred on upsertPosition(). response: ${JSON.stringify(response)}`);
  } catch (e) {
    console.log(e);
    throw e;
  }
}

const deletePosition = async (accessToken: string, userId: string, position: Position) => {
  try {
    const response = await client.delete<Position>(`/api/v0/users/${userId}/portfolios/${position.portfolioId}/positions/${position.id}`, authHeaders(accessToken));
    const status = response.status;
    if (status < 300) {
      // ok
      return true;
    }
    throw new Error(`Some problem occurred on deletePosition(). response: ${JSON.stringify(response)}`);
  } catch (e) {
    console.log(e);
    return false;
  }
}

export {
  getUser,
  createUser,
  getPortfoliosByUser,
  createPortfolio,
  upsertComposite,
  deleteComposite,
  getAssetTypes,
  getSecurities,
  getSecuritiesByCnpj,
  getLastPrice,
  getPriceToEarnings,
  getEnterpriseValueToEbitda,
  upsertPosition,
  deletePosition
};